import { ArrowDropDown, ArrowDropUp, CloseOutlined } from "@mui/icons-material";
import type { PaletteOptions, Theme } from "@mui/material/styles";
import { alpha, createTheme, ThemeOptions } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import React from "react";

export type ChipTone = "primary" | "positive" | "negative" | "neutral" | "category" | "warning";

interface ChipToneColors {
  background: string;
  border: string;
  text: string;
}

declare module "@mui/material/styles" {
  interface Theme {
    chipTones: Record<ChipTone, ChipToneColors>;
  }

  interface ThemeOptions {
    chipTones?: Record<ChipTone, ChipToneColors>;
  }
}

// Define palette
const palette: PaletteOptions = {
  primary: {
    main: "#1A75FF",
    dark: "#1565c0",
    light: "#42a5f5",
    contrastText: "#fff",
  },
  secondary: {
    main: "#9c27b0",
    dark: "#7b1fa2",
    light: "#ba68c8",
    contrastText: "#fff",
  },
  text: {
    primary: "#011638",
    secondary: "#003180",
  },
  background: {
    default: "#0066ff08",
    paper: "#FFFFFF",
  },
  divider: "#E3E8EF",
};

const chipTones: Record<ChipTone, ChipToneColors> = {
  primary: {
    background: "#EFF6FF",
    border: "#BFDBFE",
    text: "#1D4ED8",
  },
  positive: {
    background: "#F0FDF4",
    border: "#A8D5B5",
    text: "#15803D",
  },
  warning: {
    background: "#FFFBEB",
    border: "#FDE68A",
    text: "#B45309",
  },
  negative: {
    background: "#FEF2F2",
    border: "#FECACA",
    text: "#B91C1C",
  },
  neutral: {
    background: "#F8FAFC",
    border: "#CBD5E1",
    text: "#475569",
  },
  category: {
    background: "#F1F5FB",
    border: "#D9E2F0",
    text: "#6478A9",
  },
};

type StatusChipColor = "success" | "warning" | "error";

const softStatusChipStyle =
  (color: StatusChipColor) =>
  ({ theme }: { theme: Theme }) => {
    const paletteColor = theme.palette[color];
    return {
      backgroundColor: alpha(paletteColor.main, 0.1),
      border: `1px solid ${alpha(paletteColor.main, 0.3)}`,
      color: paletteColor.dark,
      "& .MuiChip-icon, & .MuiChip-deleteIcon": { color: paletteColor.dark },
    };
  };

const SortDirectionIcon = ({ className }: SvgIconProps) =>
  React.createElement(
    "span",
    { "aria-hidden": true, className },
    React.createElement(ArrowDropUp, { className: "SortDirectionIcon-up" }),
    React.createElement(ArrowDropDown, { className: "SortDirectionIcon-down" }),
  );

// Create theme configuration
const themeConfig: ThemeOptions = {
  palette,
  chipTones,
  direction: "ltr", // Default direction
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: "2.25rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.875rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // Input text in TextField/Autocomplete
        input: {
          fontSize: "16px",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectRoot: {
          fontSize: "0.875rem",
        },
        select: {
          fontSize: "0.875rem",
        },
        menuItem: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: SortDirectionIcon,
      },
      styleOverrides: {
        icon: ({ theme }) => ({
          display: "inline-flex",
          flexDirection: "column",
          // Keep the paired direction arrows visible without letting their
          // SVG view boxes determine the header column's minimum width.
          height: 32,
          justifyContent: "center",
          insetInlineStart: `calc(100% - ${theme.spacing(3)})`,
          opacity: "1 !important",
          position: "absolute",
          transform: "translateY(1px) !important",
          width: 24,
          "& .SortDirectionIcon-up, & .SortDirectionIcon-down": {
            color: theme.palette.action.disabled,
            display: "block",
            height: 24,
            opacity: 0.5,
            width: 24,
          },
          "& .SortDirectionIcon-up": {
            marginBlockEnd: -16,
          },
        }),
        root: ({ theme }) => ({
          paddingInlineEnd: theme.spacing(3),
          position: "relative",
          ".MuiTableCell-alignRight &": {
            paddingInlineEnd: 0,
            paddingInlineStart: theme.spacing(3),
          },
          ".MuiTableCell-alignRight & .MuiTableSortLabel-icon": {
            insetInlineEnd: `calc(100% - ${theme.spacing(3)})`,
            insetInlineStart: "auto",
          },
          "&:hover .SortDirectionIcon-up, &:hover .SortDirectionIcon-down, &:focus-visible .SortDirectionIcon-up, &:focus-visible .SortDirectionIcon-down":
            {
              opacity: 1,
            },
          "&.Mui-active.MuiTableSortLabel-directionAsc .SortDirectionIcon-up, &.Mui-active.MuiTableSortLabel-directionDesc .SortDirectionIcon-down":
            {
              color: theme.palette.text.primary,
              opacity: 1,
            },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Dropdown menu text (paper content)
        paper: {
          fontSize: "16px",
        },
        // Option text size
        listbox: {
          "& .MuiAutocomplete-option": {
            fontSize: "14px",
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        // Use a lighter, non-filled X icon for chip deletion by default
        deleteIcon: React.createElement(CloseOutlined, { fontSize: "small" }),
      },
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
        sizeSmall: {
          height: 22,
        },
      },
      variants: [
        {
          props: { color: "default" },
          style: {
            backgroundColor: "rgba(26, 117, 255, 0.08)",
            color: "#1A75FF",
            border: "1px solid rgba(26, 117, 255, 0.16)",
            "& .MuiChip-icon": { color: "#1A75FF" },
            "& .MuiChip-deleteIcon": { color: "#1A75FF" },
          },
        },
        {
          props: { color: "default", variant: "outlined" },
          style: {
            backgroundColor: "transparent",
            borderColor: "rgba(26, 117, 255, 0.32)",
          },
        },
        {
          props: { color: "success" },
          style: softStatusChipStyle("success"),
        },
        {
          props: { color: "warning" },
          style: softStatusChipStyle("warning"),
        },
        {
          props: { color: "error" },
          style: softStatusChipStyle("error"),
        },
      ],
    },
  },
};
// Function to create RTL theme
export const createRTLTheme = () => {
  return createTheme({
    ...themeConfig,
    direction: "rtl",
  });
};

// Function to create LTR theme
export const createLTRTheme = () => {
  return createTheme({
    ...themeConfig,
    direction: "ltr",
  });
};
